import { CheckCircle2, FilePlus2, FileText, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState, Skeleton } from "../components/ui";
import { useContracts, type ContractRow } from "../data/platform-data";
import { env } from "../lib/env";
import { subscribeDemoReset } from "../lib/demo-session";
import { useOrganization } from "../auth/OrganizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useCustomer360Directory } from "../data/supergestor-data";
import {
  canStartInternalContract,
  loadEligibleContractDocumentSnapshot,
  runContractCommand,
  contractStageFromInternalStatus,
} from "../lib/seller-contract-commands";

export type ContractStage =
  | "RASCUNHO"
  | "EM REVISÃO"
  | "APROVADO INTERNAMENTE"
  | "REJEITADO"
  | "SEM REVISÃO INTERNA"
  | "FORA DO FLUXO INTERNO";
export type ContractDraft = { customerId: string; type: string };
export type ContractErrors = Partial<Record<keyof ContractDraft, string>>;

export function contractListState(total: number, visible: number) {
  if (total === 0) return "empty" as const;
  if (visible === 0) return "no-results" as const;
  return "ready" as const;
}

type LocalContract = ContractRow & {
  customerId?: string;
  stage: ContractStage;
  versions: string[];
  baseStatus: string;
};

const emptyDraft: ContractDraft = { customerId: "", type: "" };

export function validateContractDraft(draft: ContractDraft): ContractErrors {
  const errors: ContractErrors = {};
  if (!draft.customerId) errors.customerId = "Selecione um cliente cadastrado.";
  if (!draft.type) errors.type = "Selecione o tipo de benefício.";
  return errors;
}

export function advanceContractStage(contract: LocalContract, next: ContractStage): LocalContract {
  const nextVersion = `v${contract.versions.length + 1}`;
  return {
    ...contract,
    status: next,
    stage: next,
    version: nextVersion,
    versions: [...contract.versions, nextVersion],
    updatedAt: new Intl.DateTimeFormat("pt-BR").format(new Date()),
  };
}

function stageTone(status: string) {
  if (status === "APROVADO INTERNAMENTE") return "success" as const;
  if (status === "EM REVISÃO") return "info" as const;
  if (status === "REJEITADO" || status === "FORA DO FLUXO INTERNO") return "danger" as const;
  if (status === "SEM REVISÃO INTERNA") return "neutral" as const;
  return "warning" as const;
}

function toLocalContract(contract: ContractRow): LocalContract {
  const stage: ContractStage = env.useMocks
    ? contract.status === "ATIVO" || contract.status === "ASSINADO"
      ? "APROVADO INTERNAMENTE"
      : contract.status === "AGUARDANDO ASSINATURA"
        ? "EM REVISÃO"
        : "RASCUNHO"
    : contractStageFromInternalStatus(contract.internalStatus, contract.status);
  return {
    ...contract,
    status: stage,
    stage,
    baseStatus: contract.status,
    versions: [contract.revision ? `v${contract.revision}` : contract.version],
  };
}

export function ContractsPage() {
  const query = useContracts();
  const customersQuery = useCustomer360Directory();
  const { activeOrganizationId } = useOrganization();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("TODOS");
  const [selectedNumber, setSelectedNumber] = useState<string>();
  const [showDraft, setShowDraft] = useState(false);
  const [draft, setDraft] = useState<ContractDraft>(emptyDraft);
  const [draftErrors, setDraftErrors] = useState<ContractErrors>({});
  const [draftNotice, setDraftNotice] = useState("");
  const [localContracts, setLocalContracts] = useState<LocalContract[] | null>(null);
  const [commandBusy, setCommandBusy] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [commandNotice, setCommandNotice] = useState("");

  useEffect(() => {
    if (env.useMocks && query.data && localContracts === null)
      setLocalContracts(query.data.map(toLocalContract));
  }, [localContracts, query.data]);
  useEffect(
    () =>
      subscribeDemoReset(() => {
        setLocalContracts(null);
        setSelectedNumber(undefined);
        setShowDraft(false);
        setDraft(emptyDraft);
        setDraftErrors({});
        setDraftNotice("");
        setSearch("");
        setStage("TODOS");
        setReviewNotes("");
        setCommandNotice("");
      }),
    [],
  );

  const contracts = localContracts ?? (query.data ?? []).map(toLocalContract);
  const rows = useMemo(
    () =>
      contracts.filter((contract) => {
        const term = search.trim().toLocaleLowerCase("pt-BR");
        return (
          (!term ||
            [contract.number, contract.customer, contract.type]
              .join(" ")
              .toLocaleLowerCase("pt-BR")
              .includes(term)) &&
          (stage === "TODOS" || contract.stage === stage)
        );
      }),
    [contracts, search, stage],
  );
  const selected = contracts.find((contract) => contract.number === selectedNumber) ?? rows[0];

  if (query.isLoading) return <Skeleton className="table-skeleton" />;
  if (query.error)
    return (
      <div role="alert">
        <EmptyState title="Falha ao carregar contratos" description={query.error.message} />
      </div>
    );

  function updateDraft<K extends keyof ContractDraft>(field: K, value: ContractDraft[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
    setDraftErrors((current) => ({ ...current, [field]: undefined }));
    setDraftNotice("");
  }

  function createDraft() {
    if (!env.useMocks) {
      setCommandNotice("A criação de contratos depende de um contrato elegível já existente.");
      return;
    }
    const errors = validateContractDraft(draft);
    if (Object.keys(errors).length) {
      setDraftErrors(errors);
      setDraftNotice("Revise os campos destacados antes de criar o rascunho.");
      return;
    }
    const number = `CL-DEMO-${String(contracts.length + 1).padStart(4, "0")}`;
    const created: LocalContract = {
      number,
      customer:
        customersQuery.data?.find((customer) => customer.id === draft.customerId)?.displayName ??
        "Cliente não encontrado",
      customerId: draft.customerId,
      type: draft.type,
      status: "RASCUNHO",
      stage: "RASCUNHO",
      version: "v1",
      versions: ["v1"],
      updatedAt: new Intl.DateTimeFormat("pt-BR").format(new Date()),
      baseStatus: "DRAFT",
    };
    setLocalContracts([created, ...contracts]);
    setSelectedNumber(number);
    setDraft(emptyDraft);
    setDraftErrors({});
    setDraftNotice(
      "Rascunho criado somente nesta demonstração local. Nenhuma assinatura foi solicitada.",
    );
  }

  async function moveSelected(next: ContractStage) {
    if (!selected) return;
    if (!env.useMocks) {
      if (!activeOrganizationId || !selected.id) {
        setCommandNotice("Este item não tem um identificador persistente elegível.");
        return;
      }
      const commandNames: Record<string, string> = {
        "EM REVISÃO": "command_submit_contract_internal_review",
        "APROVADO INTERNAMENTE": "command_approve_contract_internal",
        REJEITADO: "command_reject_contract_internal",
      };
      const commandName = commandNames[next];
      const revision = selected.revision;
      if (!commandName || !revision) {
        setCommandNotice("Inicie o fluxo interno para este contrato elegível antes de avançar.");
        return;
      }
      if (next === "REJEITADO" && reviewNotes.trim().length < 3) {
        setCommandNotice("Informe o motivo da rejeição interna.");
        return;
      }
      setCommandBusy(true);
      setCommandNotice("");
      try {
        await runContractCommand(commandName, {
          organizationId: activeOrganizationId,
          contractId: selected.id,
          expectedRevision: revision,
          ...(next === "REJEITADO" || next === "EM REVISÃO"
            ? { reviewNotes: reviewNotes.trim() }
            : {}),
        });
        await queryClient.invalidateQueries({ queryKey: ["scope"] });
        await query.refetch();
        setReviewNotes("");
        setCommandNotice(
          "Revisão interna atualizada no servidor. Nenhuma assinatura ou movimentação financeira foi iniciada.",
        );
      } catch (error) {
        setCommandNotice(
          error instanceof Error ? error.message : "Falha ao atualizar a revisão interna.",
        );
      } finally {
        setCommandBusy(false);
      }
      return;
    }
    setLocalContracts(
      contracts.map((contract) =>
        contract.number === selected.number ? advanceContractStage(contract, next) : contract,
      ),
    );
  }

  async function beginInternalReview(contract: LocalContract) {
    const contractId = contract.id;
    if (
      !activeOrganizationId ||
      !canStartInternalContract(contractId, contract.baseStatus) ||
      !contractId
    ) {
      setCommandNotice(
        "Somente contrato persistente em estado elegível pode iniciar uma revisão interna.",
      );
      return;
    }
    setCommandBusy(true);
    setCommandNotice("");
    try {
      await runContractCommand("command_begin_contract_internal_draft", {
        organizationId: activeOrganizationId,
        contractId,
        documentSnapshot: await loadEligibleContractDocumentSnapshot(
          activeOrganizationId,
          contractId,
        ),
      });
      await queryClient.invalidateQueries({ queryKey: ["scope"] });
      await query.refetch();
      setCommandNotice(
        "Rascunho interno criado no servidor. O status comercial do contrato permanece inalterado.",
      );
    } catch (error) {
      setCommandNotice(
        error instanceof Error ? error.message : "Falha ao iniciar a revisão interna.",
      );
    } finally {
      setCommandBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <section className="page-heading-row">
        <div>
          <p className="eyebrow">OPERAÇÃO · DOCUMENTOS</p>
          <h2>Contratos</h2>
          <p className="muted">
            Crie, revise e aprove internamente os documentos do Cruzeiro Life.
          </p>
        </div>
        <Button
          onClick={() => {
            setShowDraft((current) => !current);
            setDraftNotice("");
          }}
          disabled={!env.useMocks}
        >
          <FilePlus2 size={16} />{" "}
          {env.useMocks ? "Criar rascunho demo" : "Aguardando contrato elegível"}
        </Button>
      </section>
      <Card
        title="Contratos da operação"
        description="A aprovação nesta tela é interna. Ela não representa assinatura jurídica ou ativação externa."
      >
        {showDraft && (
          <div className="draft-panel">
            <div>
              <strong>Novo rascunho</strong>
              <span>
                {env.useMocks
                  ? "O registro ficará somente nesta sessão demo."
                  : "No modo persistente, esta tela só inicia revisão para contratos elegíveis existentes."}
              </span>
            </div>
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Cliente cadastrado</span>
                <select
                  value={draft.customerId}
                  onChange={(event) => updateDraft("customerId", event.target.value)}
                  aria-invalid={Boolean(draftErrors.customerId)}
                >
                  <option value="">Selecione um cliente da base</option>
                  {(customersQuery.data ?? []).map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.displayName}
                    </option>
                  ))}
                </select>
                {draftErrors.customerId && (
                  <span className="field-error">{draftErrors.customerId}</span>
                )}
              </label>
              <label className="field">
                <span className="field-label">Tipo de benefício</span>
                <select
                  value={draft.type}
                  onChange={(event) => updateDraft("type", event.target.value)}
                  aria-invalid={Boolean(draftErrors.type)}
                >
                  <option value="" disabled>
                    Selecione uma opção
                  </option>
                  <option>Plano funerário</option>
                  <option>Telemedicina</option>
                  <option>Funeral + telemedicina</option>
                </select>
                {draftErrors.type && <span className="field-error">{draftErrors.type}</span>}
              </label>
            </div>
            {draftNotice && (
              <p className="form-status" role="status">
                {draftNotice}
              </p>
            )}
            <div className="card-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDraft(false);
                  setDraftErrors({});
                }}
              >
                Fechar
              </Button>
              <Button onClick={createDraft} disabled={!env.useMocks}>
                Salvar rascunho
              </Button>
            </div>
          </div>
        )}
        <div className="toolbar" aria-label="Filtros de contratos">
          <label className="search-box">
            <Search size={16} aria-hidden="true" />
            <span className="sr-only">Buscar contrato</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar número, cliente ou tipo"
            />
          </label>
          <select
            value={stage}
            onChange={(event) => setStage(event.target.value)}
            aria-label="Filtrar por etapa"
          >
            <option value="TODOS">Todas as etapas</option>
            <option value="RASCUNHO">Rascunho</option>
            <option value="EM REVISÃO">Em revisão</option>
            <option value="APROVADO INTERNAMENTE">Aprovado internamente</option>
            <option value="REJEITADO">Rejeitado internamente</option>
            <option value="SEM REVISÃO INTERNA">Sem revisão interna</option>
          </select>
        </div>
        {contractListState(contracts.length, rows.length) !== "ready" ? (
          <EmptyState
            title={
              contracts.length === 0 ? "Nenhum contrato cadastrado" : "Nenhum contrato encontrado"
            }
            description={
              contracts.length === 0
                ? env.useMocks
                  ? "Crie um rascunho demonstrativo para começar a revisão interna."
                  : "A revisão interna começa sobre um contrato persistente em DRAFT, AWAITING_DATA ou AWAITING_SIGNATURE. Esta revisão não altera o estado comercial nem solicita assinatura."
                : "Ajuste a busca ou os filtros para localizar um contrato."
            }
          />
        ) : (
          <DataTable
            rows={rows}
            getKey={(contract) => contract.number}
            columns={[
              {
                key: "number",
                header: "Contrato",
                render: (contract) => (
                  <button
                    className="table-link"
                    type="button"
                    onClick={() => setSelectedNumber(contract.number)}
                  >
                    {contract.number}
                  </button>
                ),
              },
              { key: "customer", header: "Cliente", render: (contract) => contract.customer },
              { key: "type", header: "Tipo", render: (contract) => contract.type },
              {
                key: "status",
                header: "Etapa",
                render: (contract) => (
                  <Badge tone={stageTone(contract.stage)}>{contract.stage}</Badge>
                ),
              },
              { key: "version", header: "Versão", render: (contract) => contract.version },
              { key: "updated", header: "Atualizado", render: (contract) => contract.updatedAt },
            ]}
          />
        )}
      </Card>
      {selected && (
        <Card
          title={`Detalhe · ${selected.number}`}
          description={`${selected.customer} · ${selected.type}`}
        >
          <div className="three-feature-grid">
            <div className="feature-item">
              <strong>Status da revisão</strong>
              <p>
                <Badge tone={stageTone(selected.stage)}>{selected.stage}</Badge>
              </p>
            </div>
            <div className="feature-item">
              <strong>Versões</strong>
              <p>{selected.versions.join(" · ")}</p>
            </div>
            <div className="feature-item">
              <strong>Assinatura jurídica</strong>
              <p>Não solicitada nesta homologação.</p>
            </div>
          </div>
          {selected.customerId && env.useMocks && (
            <div className="inline-notice" role="note">
              <div>
                <strong>Cliente vinculado</strong>
                <span>Este rascunho demonstra o vínculo ao cadastro selecionado.</span>
              </div>
              <Link className="button button-secondary" to={`/clientes/${selected.customerId}`}>
                Abrir Customer 360
              </Link>
            </div>
          )}
          {!env.useMocks && selected.stage === "FORA DO FLUXO INTERNO" && (
            <p className="field-hint">
              A revisão interna é separada do estado comercial. Contratos aguardando assinatura
              podem ser revisados sem alterar esse estado ou solicitar assinatura.
            </p>
          )}
          <div className="card-actions">
            <Button variant="ghost" onClick={() => window.print()}>
              <FileText size={16} /> Prévia de PDF
            </Button>
            {!env.useMocks &&
              (selected.stage === "SEM REVISÃO INTERNA" ||
                (selected.stage === "RASCUNHO" && !selected.revision)) && (
                <Button
                  onClick={() => void beginInternalReview(selected)}
                  disabled={
                    commandBusy || !canStartInternalContract(selected.id, selected.baseStatus)
                  }
                >
                  {commandBusy ? "Salvando…" : "Iniciar revisão interna"}
                </Button>
              )}
            {selected.stage === "RASCUNHO" && env.useMocks && (
              <Button onClick={() => void moveSelected("EM REVISÃO")}>Enviar para revisão</Button>
            )}
            {!env.useMocks && selected.stage === "RASCUNHO" && selected.revision && (
              <Button onClick={() => void moveSelected("EM REVISÃO")} disabled={commandBusy}>
                {commandBusy ? "Enviando…" : "Enviar para revisão interna"}
              </Button>
            )}
            {selected.stage === "EM REVISÃO" && (
              <Button
                onClick={() => void moveSelected("APROVADO INTERNAMENTE")}
                disabled={commandBusy || (!env.useMocks && !selected.revision)}
              >
                <CheckCircle2 size={16} /> Aprovar internamente
              </Button>
            )}
            {!env.useMocks && selected.stage === "EM REVISÃO" && (
              <>
                <label className="field">
                  <span className="field-label">Motivo (obrigatório para rejeitar)</span>
                  <input
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    placeholder="Motivo da revisão interna"
                  />
                </label>
                <Button
                  variant="secondary"
                  onClick={() => void moveSelected("REJEITADO")}
                  disabled={commandBusy || reviewNotes.trim().length < 3}
                >
                  Rejeitar revisão
                </Button>
              </>
            )}
            {!env.useMocks && selected.stage === "REJEITADO" && (
              <span className="muted">
                Rejeição interna registrada. Reabrir exige revisão do documento e novo conteúdo.
              </span>
            )}
            {selected.stage === "APROVADO INTERNAMENTE" && (
              <span className="muted">Aprovado internamente · sem assinatura jurídica</span>
            )}
          </div>
          {commandNotice && (
            <p className="form-status" role="status">
              {commandNotice}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
